import { AdminIntro, AdminNav, AdminMetrics } from "../../components/AdminWorkspace";
import { Tabs, Tab, Typography } from "@material-ui/core";
import React, { useState, useEffect, useContext } from "react";
import { useHistory, Link as RouterLink } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import SettingsInputAntennaIcon from "@material-ui/icons/SettingsInputAntenna";
import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import usePlans from "../../hooks/usePlans";
import useUsers from "../../hooks/useUsers";
import { AuthContext } from "../../context/Auth/AuthContext";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import { filterOutboundConnections } from "../../utils/channels";
import { PageHeader, StatusBadge, MainContainer } from "../../components/ui";
import { getTokens } from "../../styles/tokens";

import KeysSection from "./KeysSection";
import Builder from "./Builder";
import Reference from "./Reference";

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    scroll: {
      flex: 1,
      minHeight: 0,
      overflowY: "auto",
      overflowX: "hidden",
      ...theme.scrollbarStyles,
    },
    page: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(1),
      maxWidth: 1280,
      margin: "0 auto",
      width: "100%",
      paddingBottom: theme.spacing(6),
      color: t.text,
    },
    // abas do tema sobre um filete, como no painel (sem cartão em volta)
    tabsBar: {
      borderBottom: `1px solid ${t.line}`,
      marginBottom: theme.spacing(1),
    },
  };
});

/**
 * Página "API" — 3 blocos: (1) chaves por conexão, (2) construtor de requisição
 * com snippets + teste real, (3) referência dos endpoints existentes.
 */
const MessagesAPI = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const { whatsApps, loading } = useContext(WhatsAppsContext);
  const { users } = useUsers();
  const { getPlanCompany } = usePlans();

  const [section, setSection] = useState("keys");
  const [queues, setQueues] = useState([]);
  const [selectedWhatsappId, setSelectedWhatsappId] = useState("");

  useEffect(() => {
    async function fetchData() {
      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (!planConfigs.plan.useExternalApi) {
        toast.error(
          "Esta empresa não possui permissão para acessar essa página! Estamos lhe redirecionando."
        );
        setTimeout(() => {
          history.push(`/`);
        }, 1000);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let alive = true;
    api
      .get("/queue")
      .then(({ data }) => alive && setQueues(Array.isArray(data) ? data : []))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  // Pré-seleciona a primeira conexão com token.
  useEffect(() => {
    if (selectedWhatsappId || !whatsApps || !whatsApps.length) return;
    const outbound = filterOutboundConnections(whatsApps);
    const first = outbound.find((w) => w.token && w.status === "CONNECTED") || outbound.find((w) => w.token);
    if (first) setSelectedWhatsappId(String(first.id));
  }, [whatsApps, selectedWhatsappId]);

  const handleUse = (wa) => {
    setSelectedWhatsappId(String(wa.id));
    setSection("builder");

  };

  const list = Array.isArray(whatsApps) ? filterOutboundConnections(whatsApps) : [];
  const withToken = list.filter((w) => w.token).length;

  return (
    <MainContainer>
      <div className={classes.scroll}>
        <div className={classes.page}>
          <AdminIntro title="API e integrações" description="Conecte seus sistemas ao atendimento. Escolha um canal, prepare a requisição e consulte os endpoints." actions={<Button variant="outlined" component={RouterLink} to="/connections" startIcon={<SettingsInputAntennaIcon />}>Gerenciar canais</Button>} />
          <AdminNav />
          <AdminMetrics items={[{ value: list.length, label: "Canais cadastrados" }, { value: withToken, label: "Canais com chave configurada" }, { value: list.filter(w => w.status === "CONNECTED").length, label: "Canais conectados" }]} />
          <div className={classes.tabsBar}>
          <Tabs value={section} onChange={(_, value) => setSection(value)} indicatorColor="primary" textColor="primary" variant="scrollable" scrollButtons="auto" aria-label="Etapas de integração API">
            <Tab value="keys" label="1. Canais e credenciais" />
            <Tab value="builder" label="2. Preparar requisição" />
            <Tab value="reference" label="3. Documentação" />
          </Tabs>
          </div>
          <section hidden={section !== "keys"} aria-label="Canais e credenciais">
          <KeysSection whatsApps={list} loading={loading} onUse={handleUse} />

          </section>
          <section hidden={section !== "builder"} aria-label="Preparar requisição">
          <Builder
            whatsApps={list}
            users={users || []}
            queues={queues}
            selectedWhatsappId={selectedWhatsappId}
            onSelectWhatsapp={setSelectedWhatsappId}
          />

          </section>
          <section hidden={section !== "reference"} aria-label="Documentação da API"><Reference /></section>
        </div>
      </div>
    </MainContainer>
  );
};

export default MessagesAPI;
